import { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import ExtensionIcon from '@mui/icons-material/ExtensionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ClearIcon from '@mui/icons-material/ClearOutlined';
import { useAuthStore } from '../auth/authStore';
import { registeredModules } from '../app/modules';
import type { ModuleMenuItem } from '../app/moduleRegistry';
import { LAYOUT } from '../theme/layout';
import { useUiPrefs } from '../theme/useUiPrefs';
import { menuItemMatchesQuery } from './sidebarSearch';

const DRAWER_WIDTH = LAYOUT.drawerWidth;
const COLLAPSED_WIDTH = LAYOUT.drawerCollapsedWidth;

/** Sidebar section order — groups not listed here (shouldn't happen) render after these. */
const GROUP_ORDER = [
  'Uçak',
  'Otel',
  'Otobüs',
  'Transfer',
  'Araç Kiralama',
  'Turlar',
  'Sigorta',
  'Kurumsal',
  'Talepler',
  'Raporlar',
  'Pos',
  'Sistem',
  'Loglar',
  'B2C Yönetimi',
  'Yardım',
];

export function Sidebar() {
  const location = useLocation();
  const currentUser = useAuthStore((s) => s.currentUser);

  const moduleMenuItems = registeredModules.flatMap((m) => m.menuItems);
  const visibleItems = moduleMenuItems.filter((item) => item.isVisible?.(currentUser) ?? true);

  const { groups, standaloneItems } = useMemo(() => {
    const byGroup = new Map<string, ModuleMenuItem[]>();
    const standalone: ModuleMenuItem[] = [];

    for (const item of visibleItems) {
      if (!item.group) {
        standalone.push(item);
        continue;
      }
      const list = byGroup.get(item.group) ?? [];
      list.push(item);
      byGroup.set(item.group, list);
    }

    const orderedGroupNames = [
      ...GROUP_ORDER.filter((name) => byGroup.has(name)),
      ...[...byGroup.keys()].filter((name) => !GROUP_ORDER.includes(name)),
    ];

    const orderedGroups = orderedGroupNames.map((name) => {
      const items = byGroup.get(name)!;
      return {
        name,
        icon: items.find((i) => i.groupIcon)?.groupIcon ?? <ExtensionIcon />,
        items,
      };
    });

    return { groups: orderedGroups, standaloneItems: standalone };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems.length, currentUser]);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');
  const collapsed = useUiPrefs((s) => s.sidebarCollapsed);
  const setCollapsed = useUiPrefs((s) => s.setSidebarCollapsed);
  const toggleCollapsed = useUiPrefs((s) => s.toggleSidebar);

  const searching = query.trim().length > 0 && !collapsed;

  const isGroupOpen = (name: string) => openGroups[name] ?? false;
  const toggleGroup = (name: string) =>
    setOpenGroups((prev) => ({ ...prev, [name]: !isGroupOpen(name) }));

  /** Daraltılmışken bir grup ikonuna tıklamak, o grubu açık hâlde menüyü tekrar
   *  genişletir — daraltılmış hâlde alt menüleri göstermeye yer olmadığından. */
  const handleGroupClick = (name: string) => {
    if (collapsed) {
      setCollapsed(false);
      if (!isGroupOpen(name)) toggleGroup(name);
      return;
    }
    toggleGroup(name);
  };

  /** Arama aktifken: grup adı eşleşirse tüm öğeleri; değilse yalnız eşleşen öğeleri.
   *  Arama yoksa grubun tüm öğeleri döner. */
  const itemsForGroup = (groupName: string, items: ModuleMenuItem[]): ModuleMenuItem[] => {
    if (!searching) return items;
    if (menuItemMatchesQuery(query, groupName)) return items;
    return items.filter((i) => menuItemMatchesQuery(query, i.label));
  };

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shortest }),
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          overflowX: 'hidden',
          transition: (t) => t.transitions.create('width', { duration: t.transitions.duration.shortest }),
        },
      }}
    >
      {/* Boş spacer — Topbar `position: fixed` olduğundan bu şeridin üstünü kaplıyor;
          görünür içerik bunun ALTINDA render edilmeli. */}
      <Toolbar />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 0.5 : 1.5,
          pt: 1,
        }}
      >
        {!collapsed && (
          <TextField
            size="small"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Menüde ara…"
            aria-label="Menüde ara"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQuery('')} aria-label="Aramayı temizle" edge="end">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              },
            }}
          />
        )}
        <IconButton onClick={toggleCollapsed} size="small" aria-label={collapsed ? 'Menüyü genişlet' : 'Menüyü daralt'}>
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Box>

      <List component="nav" sx={{ px: collapsed ? 0.5 : 1.5, py: 1.5 }}>
        {!searching && (
          <Tooltip title={collapsed ? 'Anasayfa' : ''} placement="right">
            <ListItemButton
              component={RouterLink}
              to="/"
              selected={location.pathname === '/'}
              sx={{ borderRadius: 2, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                <DashboardIcon />
              </ListItemIcon>
              {!collapsed && <ListItemText primary="Anasayfa" slotProps={{ primary: { variant: 'body2' } }} />}
            </ListItemButton>
          </Tooltip>
        )}

        {groups.map((group) => {
          const items = itemsForGroup(group.name, group.items);
          if (searching && items.length === 0) return null;
          const open = !collapsed && (searching || isGroupOpen(group.name));
          return (
            <div key={group.name}>
              <Tooltip title={collapsed ? group.name : ''} placement="right">
                <ListItemButton
                  onClick={() => handleGroupClick(group.name)}
                  sx={{ borderRadius: 2, mt: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
                >
                  <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                    {group.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={
                          <Typography
                            variant="overline"
                            sx={{ fontWeight: 700, letterSpacing: '0.06em', lineHeight: 2 }}
                          >
                            {group.name}
                          </Typography>
                        }
                      />
                      {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {items.map((item) => (
                    <ListItemButton
                      key={item.path}
                      component={RouterLink}
                      to={`/${item.path}`}
                      selected={location.pathname === `/${item.path}`}
                      sx={{ borderRadius: 2, pl: 3.5, ml: 1, mb: 0.25, py: 0.5 }}
                    >
                      <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            </div>
          );
        })}

        {(() => {
          const items = searching
            ? standaloneItems.filter((i) => menuItemMatchesQuery(query, i.label))
            : standaloneItems;
          if (items.length === 0) return null;
          return (
            <>
              <Divider sx={{ my: 1.5 }} />
              {items.map((item) => (
                <Tooltip key={item.path} title={collapsed ? item.label : ''} placement="right">
                  <ListItemButton
                    component={RouterLink}
                    to={`/${item.path}`}
                    selected={location.pathname === `/${item.path}`}
                    sx={{ borderRadius: 2, mb: 0.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
                  >
                    <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                      {item.icon ?? <ExtensionIcon />}
                    </ListItemIcon>
                    {!collapsed && <ListItemText primary={item.label} slotProps={{ primary: { variant: 'body2' } }} />}
                  </ListItemButton>
                </Tooltip>
              ))}
            </>
          );
        })()}

        {searching &&
          groups.every((g) => itemsForGroup(g.name, g.items).length === 0) &&
          standaloneItems.filter((i) => menuItemMatchesQuery(query, i.label)).length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
              Eşleşen menü yok.
            </Typography>
          )}
      </List>
    </Drawer>
  );
}
